import React from 'react';
import { Classable, HasChildren } from '@shapeable/types';
import styled, { css } from 'styled-components';
import { breakpoints, theme } from '@shapeable/theme';
import { PageContextDefinition, UiConfig, LayoutShell, SiteFooterLayout, UiProvider, ViewProvider, EntityExplorerLayoutWithoutFooter, Dialogs, useSite, KnowledgeHubPageProvider, ComponentSwapProvider, CookieBannerMessageProvider, useAuth, UserMenuProvider, useActiveEntity } from '@shapeable/ui';
import * as pageLayouts from './page-layouts';
import * as sliceLayouts from './slice-layouts';
import * as indicatorLayouts from './indicator-layouts';
import { LANG_STRINGS } from '../lang';
import { BEAUTIFUL_BUFF, DARKER_PRIMARY, LayoutStyles, themeOverrides } from '../theme';
import { VideoCard } from './entities/video-card';
import * as EntityPageQueries from '../graphql/details';
import { TrendsProvider } from '../gatsby/providers/trends-provider';
import { TrendsExplorerLayout } from './entities/trends-explorer-layout';
import { LinkEntityButton } from './elements/link-entity-button';
import { EntityLinkButton } from './elements/entity-link-button';
import { CookieBannerMessage } from './elements/cookie-banner-message';
import { menuItemIsAvailable } from './utils/menu-item-is-available';
import { UserMenuItems } from './elements/user-menu-items';
import { CitiesIcon } from './icons/cities-icon';
import { CountriesIcon } from './icons/countries-icon';
import { MembersIcon } from './icons/members-icon';
import { includes } from 'lodash';
import { BusIconGlyph } from './icons/bus-icon';
import { IdeaIconGlyph } from './icons/idea-icon';
import { CreativityIconGlyph } from './icons/creativity-icon';
import { EnergyIconGlyph } from './icons/energy-icon';
import { ScienceIconGlyph } from './icons/science-icon';
import { WaterwaysIconGlyph } from './icons/waterways-icon';
import { FoodIconGlyph } from './icons/food-icon';
import { TransportIconGlyph } from './icons/transport-icon';
import { FcHandIconGlyph } from '@shapeable/icons';
import { AncestorsIconGlyph } from './icons/ancestors-icon';
import { FixingFashionIconGlyph } from './icons/fixing-fashion-icon';


export type LayoutPropTypes = Classable & HasChildren & {
  path?: string;
  data?: any;
  pageContext?: PageContextDefinition;
};

const LayoutDefaultProps: LayoutPropTypes = {
};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    ${LayoutStyles};
  `,
});

const FooterStyles = breakpoints({
  base: css`
  `,
});

const BodyStyles = breakpoints({
  base: css`
    flex-grow: 1;
  `,
});

// -------- Components -------->

const My = {
  Container: styled(LayoutShell)`${ContainerStyles}`,
  Body: styled.div`${BodyStyles}`,
  Footer: styled(SiteFooterLayout)`${FooterStyles}`,
};

export const Layout: React.FC<LayoutPropTypes> = (props) => {
  const { className, children } = props;

  const site = useSite();
  const auth = useAuth();
  const entity = useActiveEntity();
  const { knowledgeHubPage } = site || {};

  const showFooter = includes('Post', entity?.entityTypeName) || !entity?.entityPath;

  const uiConfig: UiConfig = {
    layouts: {
      pageLayouts,
      sliceLayouts,
      indicatorLayouts,
      defaultPageLayout: pageLayouts.PageLayoutDefault,
    },
    langStrings: LANG_STRINGS,
    theme: themeOverrides,
    icons: {
      CitiesIcon,
      CountriesIcon,
      MembersIcon,
      BusIconGlyph,
      IdeaIconGlyph,
      CreativityIconGlyph,
      EnergyIconGlyph,
      ScienceIconGlyph,
      WaterwaysIconGlyph,
      FoodIconGlyph,
      TransportIconGlyph,
      FcHandIconGlyph,
      AncestorsIconGlyph,
      FixingFashionIconGlyph,
    },
    search: {
      isVisible: false,
      isAvailable: false,
    },
    header: {
      variant: 'overlay',
      menuItemIsAvailable: (menuItemIsAvailable(auth))
    },
    menuBar: {
      boundary: 'content',
      
    },
    explorer: {
      defaultView: 'home',
      authenticationRequired: true,
    },
    entity: {
      cards: {
        Video: VideoCard,
      },
      dataProviders: [
        TrendsProvider,
      ],
      layouts: {
        Person: EntityExplorerLayoutWithoutFooter,
        Trend: TrendsExplorerLayout,
      },
      pageQueries: EntityPageQueries,
    },
    componentProps: {
      IndicatorGroupLayoutGrid: {
        rowWeight: 4,
        tabletRowWeight: 2,
        countIndicators: 'all',
      },
      ContentEntityLayout: {
        onlyShowAuthors: true,
      },
      Comment: {
        showOrganisation: false,
      },
      OnboardingButton: {
        variantColors: {
          primary: {
            background: 'primary',
            text: 'dark',
            hover: {
              background: 'primary',
              text: 'dark',
            }
          },
        }
      },
      SiteMenuBar: {
        colors: {
          background: BEAUTIFUL_BUFF,
        }
      },
      EntityExplorerLayout: {
        showSiteFooter: () => false,
      },
      MenuItem: {
        colors: {
          hover: {
            text: 'light',
          }
        }
      },
      SiteFooterLayout: {
        colors: {
          background: '#1d1714',
        }
      },
      SiteHeaderUtilityButton: {
        isMenuInverted: true,
        colors: {
          background: 'primary',
          hover: {
            background: DARKER_PRIMARY,
          }
        }
      }
    }
  }

  return (
    <ViewProvider>
    <UiProvider value={uiConfig}>
      <KnowledgeHubPageProvider value={knowledgeHubPage}>
      <UserMenuProvider value={{
        userMenuBottomItem: UserMenuItems,
      }}>
      <ComponentSwapProvider value={{
        LinkEntityButton: LinkEntityButton,
        EntityLinkButton: EntityLinkButton,
      }}>
      <CookieBannerMessageProvider value={CookieBannerMessage}>
      <My.Container
        className={className}
        includeTooltip
        tooltipProps={{
          backgroundColor: '#FFFFFF',
        }}
      >
        <My.Body>
          {
            auth.isAuthenticated && <Dialogs />
          }
          {children}
        </My.Body>
        {
          showFooter && <My.Footer />
        }
      </My.Container>
      </CookieBannerMessageProvider>
      </ComponentSwapProvider>
      </UserMenuProvider>
      </KnowledgeHubPageProvider>
    </UiProvider>
    </ViewProvider>
  );
  
};

Layout.defaultProps = LayoutDefaultProps;

